/**
 * Google Ads connect card — unified across onboarding + integrations.
 *
 * Phase 5 refactor (2026-05-30):
 *   - Deleted the standalone "Skipped" branch. One card, two states
 *     (connected / not-connected). Skip is just a checkbox on the
 *     not-connected card; when checked the box shows ✓ but the card
 *     visual otherwise stays the same. To clear: press Connect.
 *   - `willSkip` / `onWillSkipChange` stay as controlled props. Onboarding
 *     uses deferred local-intent (parent commits via /skip on Continue).
 *     Integrations passes an immediate-POST handler.
 *   - `showSkipOption` dropped — checkbox always renders in the
 *     not-connected state.
 *   - New `showFireCounts` prop for integrations chrome (today/last-30
 *     conversions fired). When provided, renders the count strip in the
 *     connected state.
 */

import { useState } from 'react';
import { CheckCircle2, Info } from 'lucide-react';

import { useAuth } from '@/lib/useAuth';
import { apiRequest } from '@/lib/api';
import { platformLogo } from '@/lib/attributionDisplay';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

interface GoogleAdsCardProps {
  /** Amber heads-up banner (two permission boxes). Onboarding only. */
  showOAuthHeadsUp?: boolean;
  /** Checkbox value. Reflects `me.google_ads_skipped_at` for integrations,
   *  or parent's local willSkip state for onboarding's deferred commit. */
  willSkip?: boolean;
  /** Called when user toggles the checkbox. Onboarding sets local state;
   *  integrations should POST /onboarding/skip immediately. */
  onWillSkipChange?: (next: boolean) => void;
  /** Integrations chrome: today + last-30 conversions fired strip in the
   *  connected state. */
  showFireCounts?: boolean;
  firesToday?: number;
  firesLast30d?: number;
}

export function GoogleAdsCard({
  showOAuthHeadsUp = false,
  willSkip = false,
  onWillSkipChange,
  showFireCounts = false,
  firesToday,
  firesLast30d,
}: GoogleAdsCardProps = {}) {
  const { me } = useAuth();
  const [busy, setBusy] = useState<'connect' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const customerId = me?.google_ads_customer_id ?? null;

  async function startConnect() {
    setBusy('connect');
    setError(null);
    try {
      const res = await apiRequest<{ auth_url: string }>(
        '/oauth/google-ads/start',
      );
      window.location.href = res.auth_url;
    } catch {
      setError('Could not start the Google Ads connection. Please try again.');
      setBusy(null);
    }
  }

  // Connected — green confirmation card.
  if (customerId) {
    return (
      <div className="rounded-3xl border border-green-200 bg-card p-7 shadow-card sm:p-9">
        <div className="flex items-start gap-4">
          <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-green-700" />
          <div className="min-w-0 flex-1">
            <h3 className="text-xl font-bold text-dark">
              Google Ads connected
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Account ID: <code className="font-mono">{customerId}</code>
            </p>
            <button
              type="button"
              onClick={startConnect}
              disabled={busy === 'connect'}
              className="mt-2 text-xs font-medium text-green-800 underline underline-offset-2 hover:no-underline disabled:opacity-60"
            >
              Reconnect
            </button>
            {showFireCounts && (
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <span>
                  <span className="font-bold text-dark">{firesToday ?? 0}</span>{' '}
                  <span className="text-muted-foreground">fired today</span>
                </span>
                <span className="text-dark/20">·</span>
                <span>
                  <span className="font-bold text-dark">
                    {(firesLast30d ?? 0).toLocaleString()}
                  </span>{' '}
                  <span className="text-muted-foreground">last 30 days</span>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Not connected — Connect button + skip checkbox. The checkbox is
  // always visible in this state; its checked value comes from `willSkip`.
  return (
    <div className="rounded-3xl bg-card p-7 shadow-card sm:p-9">
      <div className="flex items-start gap-5">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-muted">
          <img
            src={platformLogo('google-ads')}
            alt=""
            className="h-7 w-7"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-xl font-bold text-dark">Connect Google Ads</h3>
          <p className="mt-2 text-base leading-relaxed text-muted-foreground">
            We'll create three conversion actions on your account (Lead,
            Qualified, Purchase) and turn on Enhanced Conversions. Google starts
            crediting real customers to your campaigns.
          </p>
          {showOAuthHeadsUp && (
            <div className="mt-5">
              <Banner variant="warn" size="compact" icon={Info}>
                On the next screen Google asks you to check two permission
                boxes. <strong>Check both.</strong>
              </Banner>
            </div>
          )}
          {error && (
            <div className="mt-5">
              <Banner variant="error" size="compact">
                {error}
              </Banner>
            </div>
          )}
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <Button
              type="button"
              onClick={startConnect}
              disabled={busy !== null}
              className="rounded-full bg-dark px-6 py-3 font-bold text-white hover:bg-dark/90"
            >
              {busy === 'connect' ? 'Opening Google…' : 'Connect Google Ads'}
            </Button>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={willSkip}
                disabled={busy !== null}
                onCheckedChange={(checked) =>
                  onWillSkipChange?.(checked === true)
                }
              />
              <span>I don't run Google Ads</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
